import type { SxProps, Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC, ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CommonDashboardBox } from "../../../components/Dashboard/CommonDashboardBox";
import { CommonDashboardHeader } from "../../../components/Dashboard/CommonDashboardHeader";
import { InHouseLineChart } from "../../../components/UIToolkit/Graphs/LineChart/InHouseLineChart";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { ALPHA, CHART_REFERENCE } from "../../../theme/tokens";
import type { ManagementCenterPPDResponse } from "../../../utils/models";
import { type ClampRange, fetchedDataToChartDataGroup, getCensusYDomain } from "../utils";

type Props = {
	data?: ManagementCenterPPDResponse;
	isLoading: boolean;
	trailing?: ReactNode;
	containerSx?: SxProps<Theme>;
	shrinkLegendForSmallScreens?: boolean;
	/** Selected range; clamps edge weekly buckets so the chart never plots a point outside the selection. */
	clampRange?: ClampRange;
};

export const PPDOverTime: FC<Props> = ({ data, isLoading, trailing, containerSx, shrinkLegendForSmallScreens, clampRange }) => {
	const { timezone, chosenDepartment } = useHospitalData();
	const navigate = useNavigate();
	const { track } = useAnalytics();
	const [searchParams] = useSearchParams();

	const handleClick = () => {
		const params = new URLSearchParams(searchParams);
		params.set("metric", "ppd");
		track("schedule_vs_actual_report_opened", { metric: "ppd", department_id: chosenDepartment.department_id });
		navigate(`/${chosenDepartment.department_id}/schedule-vs-actual?${params.toString()}`);
	};

	const targetPpdData = data?.targetPpd;
	const hasTarget = targetPpdData && Object.keys(targetPpdData).length > 0;

	const transformedDataLeft =
		!isLoading && data
			? [
					fetchedDataToChartDataGroup({
						data: data.ppdScheduled,
						name: "SCHEDULED PPD",
						timezone,
						clampRange,
						color: CHART_REFERENCE.SCHEDULED,
					}),
					fetchedDataToChartDataGroup({
						data: data.ppdActual,
						name: "ACTUAL PPD",
						timezone,
						clampRange,
						color: CHART_REFERENCE.ACTUAL,
					}),
					...(hasTarget
						? [
								fetchedDataToChartDataGroup({
									data: targetPpdData,
									name: "TARGET",
									timezone,
									clampRange,
									color: CHART_REFERENCE.TARGET,
									strokeDasharray: CHART_REFERENCE.TARGET_DASH,
								}),
							]
						: []),
				]
			: undefined;

	const transformedDataRight =
		!isLoading && data && transformedDataLeft
			? [
					fetchedDataToChartDataGroup({
						data: data.census,
						name: "CENSUS",
						timezone,
						clampRange,
						color: CHART_REFERENCE.CENSUS,
					}),
				]
			: undefined;

	const targetValues = hasTarget ? Object.values(targetPpdData) : [];
	const maxPPD = data ? Math.round(Math.max(...Object.values(data.ppdActual), ...Object.values(data.ppdScheduled), ...targetValues) + 3) : 0;
	const minPPD = data ? Math.max(Math.floor(Math.min(...Object.values(data.ppdActual), ...Object.values(data.ppdScheduled), ...targetValues) - 3), 0) : 0;
	const censusDomain = getCensusYDomain(data?.census);

	return (
		<CommonDashboardBox
			onClick={handleClick}
			sx={
				((theme) => ({
					display: "flex",
					flexDirection: "column",
					alignItems: "start",
					gap: 1,
					width: "calc(33% - 4px)",
					...containerSx,
					cursor: "pointer",
					"&:hover": { boxShadow: `0px 2px 12px 0px ${alpha(theme.palette.primary.main, ALPHA.FOCUS_RING)}` },
					transition: "box-shadow 0.2s",
				})) as SxProps<Theme>
			}
		>
			<CommonDashboardHeader label={"PPD Over Time"} trailing={trailing} />
			<InHouseLineChart
				shrinkLegendForSmallScreens={shrinkLegendForSmallScreens}
				margin={{
					top: 4,
					right: 0,
					bottom: 0,
					left: 20,
				}}
				width={"100%"}
				height={containerSx ? "100%" : undefined}
				leftAxisData={
					transformedDataLeft
						? {
								dataGroups: transformedDataLeft,
								maxValue: maxPPD,
								minValue: minPPD,
								tickRenderer: (value) => {
									return `$${value.toFixed(2)}`;
								},
							}
						: undefined
				}
				rightAxisData={
					transformedDataRight
						? {
								dataGroups: transformedDataRight,
								maxValue: censusDomain?.max,
								minValue: censusDomain?.min,
								tickRenderer: (value) => {
									return `${value}${value === censusDomain?.max ? "🧑" : ""}`;
								},
							}
						: undefined
				}
				tooltipSortOrder={{ "SCHEDULED PPD": 0, "ACTUAL PPD": 1, TARGET: 2, CENSUS: 3 }}
			/>
		</CommonDashboardBox>
	);
};
